import React, { useState, useRef, useEffect } from 'react';
import { useSpace } from '../../contexts/SpaceContext';
import { Trash2, Link2, Download, Check, Share2, Link2Off } from 'lucide-react';
import { generateICS, downloadICS } from '../../utils/calendar/icsGenerator';
import { ColorPicker } from './ColorPicker';
import { track } from '../../utils/analytics';

interface CategoryEvent {
  id: number | string;
  title: string;
  dates: string[];
  notes?: string;
  all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
}

interface CategoryQuickMenuProps {
  isOpen: boolean;
  onClose: () => void;
  category: {
    id?: string;
    name: string;
    color: string;
    is_shared?: boolean;
    share_token?: string | null;
  };
  isNew?: boolean;
  onUpdate: (id: string, updates: { name?: string; color?: string; is_shared?: boolean; share_token?: string | null }) => Promise<any>;
  onCreate?: (data: { name: string; color: string }) => Promise<any>;
  onDelete: (id: string) => Promise<void>;
  anchorElement: HTMLButtonElement;
  categoryEvents?: CategoryEvent[]; // Events using this category (for deletion warning and export)
  onNavigateToYear?: (year: number) => void; // Callback to navigate to a specific year
  userId?: string; // User ID for subscription URL
}

export default function CategoryQuickMenu({
  isOpen,
  onClose,
  category,
  isNew = false,
  onUpdate,
  onCreate,
  onDelete,
  anchorElement,
  categoryEvents = [],
  onNavigateToYear,
  userId,
}: CategoryQuickMenuProps) {
  const [name, setName] = useState(category.name);
  const [color, setColor] = useState(category.color);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const { isPersonalSpace } = useSpace();
  const menuRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset state and capture position when menu opens
  useEffect(() => {
    if (isOpen && anchorElement) {
      setName(category.name);
      setColor(category.color);
      setHasChanges(false);

      const rect = anchorElement.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = 360;
      const gap = 8;
      
      let top = rect.top;
      let left = rect.right + gap;
      
      if (left + menuWidth > window.innerWidth - 8) {
        left = rect.left - menuWidth - gap;
      }
      
      if (top + menuHeight > window.innerHeight - 8) {
        top = window.innerHeight - menuHeight - 8;
      }
      if (top < 8) top = 8;
      
      setMenuPosition({ top, left });
    }
  }, [category.id, category.name, category.color, isOpen, anchorElement]);

  // Focus name input when opening
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
          const len = nameInputRef.current.value.length;
          nameInputRef.current.setSelectionRange(len, len);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const saveChanges = async (updates: { name?: string; color?: string }) => {
    if (isNew && onCreate) {
      const newName = updates.name || name;
      const newColor = updates.color || color;
      if (newName.trim()) {
        await onCreate({ name: newName.trim(), color: newColor });
        onClose();
      }
    } else if (category.id) {
      await onUpdate(category.id, updates);
    }
  };

  const handleNameBlur = () => {
    if (isNew) {
      return;
    }
    if (name !== category.name && name.trim()) {
      saveChanges({ name: name.trim() });
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isNew && name.trim()) {
        saveChanges({ name: name.trim(), color });
      } else {
        handleNameBlur();
        (e.target as HTMLInputElement).blur();
      }
    }
  };

  const handleColorSelect = (newColor: string) => {
    setColor(newColor);
    setHasChanges(true);
    if (!isNew && category.id) {
      saveChanges({ color: newColor });
    }
  };

  const handleDeleteClick = () => {
    if (!category.id) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!category.id) return;
    await onDelete(category.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleYearClick = (year: number) => {
    if (onNavigateToYear) {
      onNavigateToYear(year);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  // Group events by year for the confirmation modal
  const getEventsByYear = () => {
    const eventsByYear = new Map<number, CategoryEvent[]>();
    for (const event of categoryEvents) {
      const year = event.dates[0] ? parseInt(event.dates[0].split('-')[0]) : new Date().getFullYear();
      if (!eventsByYear.has(year)) {
        eventsByYear.set(year, []);
      }
      eventsByYear.get(year)!.push(event);
    }
    return eventsByYear;
  };

  const handleCreate = async () => {
    if (isNew && name.trim() && onCreate) {
      await onCreate({ name: name.trim(), color });
      onClose();
    }
  };

  const getMenuStyle = (): React.CSSProperties => {
    if (!menuPosition) return { top: 0, left: 0 };
    return menuPosition;
  };

  // Generate subscription URL. The share token is the only credential, so a
  // freshly generated one can be passed in before it has been persisted.
  const getSubscriptionUrl = (token?: string | null): string | null => {
    const shareToken = token ?? category.share_token;
    if (!shareToken) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/calendar/${shareToken}.ics`;
  };

  // Copy subscription URL to clipboard
  const handleCopySubscriptionUrl = async () => {
    const url = getSubscriptionUrl();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Export events as ICS file
  const handleExportICS = () => {
    if (categoryEvents.length === 0) return;

    const icsContent = generateICS(categoryEvents, category.name);
    const filename = `${category.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`;
    downloadICS(icsContent, filename);
  };

  // Toggle sharing
  const handleToggleSharing = async () => {
    if (!category.id) return;
    const newSharedState = !category.is_shared;

    // Mint a new token on every enable so the previous URL stops working —
    // disabling and re-enabling is how the user resets a leaked link.
    const nextToken = newSharedState ? crypto.randomUUID() : null;

    // If enabling sharing, copy URL to clipboard BEFORE async operation
    // (clipboard API requires immediate user gesture context)
    if (newSharedState) {
      const url = getSubscriptionUrl(nextToken);
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          setCopiedUrl(true);
          setTimeout(() => setCopiedUrl(false), 2000);
        } catch (err) {
          console.error('Failed to copy URL:', err);
        }
      }
    }

    // Then update the database
    await onUpdate(category.id, { is_shared: newSharedState, share_token: nextToken });
    if (newSharedState) track('share_link_created');
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[99]"
        onMouseDown={(e) => {
          if (menuRef.current && menuRef.current.contains(e.target as Node)) {
            return;
          }
          onClose();
        }}
      />
      
      <div
        ref={menuRef}
        className="fixed bg-[var(--modal-bg)] rounded-lg shadow-lg border border-[var(--border-primary)] w-56 z-[100] animate-fadeIn"
        style={getMenuStyle()}
      >
        {/* Name input */}
        <div className="p-2 border-b border-[var(--border-secondary)]">
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v.length === 1 ? v.toUpperCase() : v);
              setHasChanges(true);
            }}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            className="w-full px-2 py-1 text-sm border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
            placeholder="Calendar name"
          />
        </div>

        {/* Color picker */}
        <div className="p-2 border-b border-[var(--border-secondary)]">
          <div className="text-[10px] text-[var(--content-secondary)] uppercase tracking-wide mb-1.5">Color</div>
          <ColorPicker
            value={color}
            onChange={handleColorSelect}
          />
        </div>

        {/* Actions */}
        <div className="p-1 space-y-0.5">
          {isNew ? (
            <button
              type="button"
              onClick={handleCreate}
              disabled={!name.trim()}
              className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-xs text-[var(--content-inverted)] bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Category
            </button>
          ) : (
            <>
              {/* Sharing toggle */}
              {userId && category.id && (
                <button
                  type="button"
                  onClick={handleToggleSharing}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                    category.is_shared
                      ? 'text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)]'
                      : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
                  }`}
                >
                  {copiedUrl ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-[var(--success)]" />
                      <span className="text-[var(--success)]">URL copied!</span>
                    </>
                  ) : category.is_shared ? (
                    <>
                      <Link2Off className="w-3.5 h-3.5" />
                      Disable sharing
                    </>
                  ) : (
                    <>
                      <Share2 className="w-3.5 h-3.5" />
                      Share calendar
                    </>
                  )}
                </button>
              )}

              {/* Publishing a space calendar exposes everyone's entries in it,
                  not just the owner's — say so before the switch is flipped. */}
              {userId && category.id && !isPersonalSpace && !category.is_shared && (
                <p className="px-2 pb-1 pt-0.5 text-[10px] leading-snug text-[var(--content-tertiary)]">
                  This calendar is in a shared space. Publishing it makes every
                  member's events in it readable by anyone with the link.
                </p>
              )}

              {/* Copy URL (only shown when sharing is enabled) */}
              {userId && category.id && category.is_shared && category.share_token && (
                <button
                  type="button"
                  onClick={handleCopySubscriptionUrl}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Copy subscription URL
                </button>
              )}

              {/* Export ICS */}
              {categoryEvents.length > 0 && (
                <button
                  type="button"
                  onClick={handleExportICS}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export as .ics
                </button>
              )}

              {/* Delete */}
              <button
                type="button"
                onClick={handleDeleteClick}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-[var(--error)] hover:bg-[var(--status-error-bg)] rounded transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete calendar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[110] p-4 animate-fadeInBackdrop">
          <div className="bg-[var(--modal-bg)] rounded-2xl w-full max-w-md p-6 shadow-xl animate-fadeIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[var(--error)]/10 rounded-lg">
                <Trash2 className="w-5 h-5 text-[var(--error)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--content-primary)]">Delete Calendar</h3>
            </div>

            <div className="mb-6">
              <p className="text-[var(--content-secondary)] mb-3">
                Delete "<span className="font-medium text-[var(--content-primary)]">{category.name}</span>" category?
              </p>

              {categoryEvents.length > 0 ? (
                <>
                  <p className="text-sm text-[var(--content-tertiary)] mb-3">
                    {categoryEvents.length} event{categoryEvents.length === 1 ? '' : 's'} will become unassigned:
                  </p>
                  <div className="max-h-48 overflow-y-auto space-y-3">
                    {Array.from(getEventsByYear().entries())
                      .sort(([a], [b]) => a - b)
                      .map(([year, events]) => (
                        <div key={year} className="text-sm">
                          <button
                            onClick={() => handleYearClick(year)}
                            className="font-medium text-[var(--accent-primary)] hover:underline cursor-pointer"
                          >
                            {year}
                          </button>
                          <span className="text-[var(--content-tertiary)]">
                            {' '}— {events.length} event{events.length === 1 ? '' : 's'}
                          </span>
                          <div className="mt-1 text-[var(--content-tertiary)] pl-2 border-l-2 border-[var(--border-secondary)]">
                            {events.slice(0, 3).map((event, i) => (
                              <div key={event.id} className="truncate">
                                {event.title}
                              </div>
                            ))}
                            {events.length > 3 && (
                              <div className="text-[var(--content-tertiary)] italic">
                                +{events.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-[var(--content-tertiary)]">
                  No events are using this category.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-secondary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-[var(--error)] text-white rounded-lg hover:opacity-90 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}